import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { NotificationWelcomeModalComponent } from '@components/modals/notification-welcome-modal/notification-welcome-modal.component';

import { NotificationService } from '@services/notification.service';
import { ErrorDialogService } from '@services/error-dialog.service';
import { NotificationsAdminActions, NotificationsUserActions } from '@store/actions/notifications/notifications.actions';
import { selectShowInactive, selectWelcomeModalShown } from '@store/selectors/notifications/notifications.selectors';
import { selectUserOrgId } from '@store/selectors/user/user.selectors';
import { UserActions } from '@store/actions/user/user.actions';
import { AuthActions } from '@store/actions/auth/auth.actions';

@Injectable()
export class NotificationsEffects {
  private isInitialLogin = false;

  trackInitialLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(() => { this.isInitialLogin = true; })
      ),
    { dispatch: false }
  );

  loadUserNotificationsOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setUserOrganizationId),
      map(() => NotificationsUserActions.loadUserNotifications())
    )
  );

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.loadNotifications),
      switchMap(({ showInactive }) =>
        this.notificationService.getNotifications(showInactive).pipe(
          map((notifications) => NotificationsAdminActions.loadNotificationsSuccess({ notifications })),
          catchError((error) =>
            of(NotificationsAdminActions.loadNotificationsFailure({
              error: error.message || 'Failed to load notifications',
            }))
          )
        )
      )
    )
  );

  loadNotificationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsAdminActions.loadNotificationsFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to load notifications. Please try again.',
            'Notifications Error'
          );
        })
      ),
    { dispatch: false }
  );

  createNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.createNotification),
      switchMap(({ request }) =>
        this.notificationService.createNotification(request).pipe(
          map((notification) => NotificationsAdminActions.createNotificationSuccess({ notification })),
          catchError((error) =>
            of(NotificationsAdminActions.createNotificationFailure({
              error: error.message || 'Failed to create notification',
            }))
          )
        )
      )
    )
  );

  createNotificationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.createNotificationSuccess),
      withLatestFrom(this.store.select(selectShowInactive)),
      map(([, showInactive]) => NotificationsAdminActions.loadNotifications({ showInactive }))
    )
  );

  createNotificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsAdminActions.createNotificationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to create notification. Please try again.',
            'Create Notification Error'
          );
        })
      ),
    { dispatch: false }
  );

  updateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.updateNotification),
      switchMap(({ request }) =>
        this.notificationService.updateNotification(request).pipe(
          map((notification) => NotificationsAdminActions.updateNotificationSuccess({ notification })),
          catchError((error) =>
            of(NotificationsAdminActions.updateNotificationFailure({
              error: error.message || 'Failed to update notification',
            }))
          )
        )
      )
    )
  );

  updateNotificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsAdminActions.updateNotificationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to update notification. Please try again.',
            'Update Notification Error'
          );
        })
      ),
    { dispatch: false }
  );

  deleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.deleteNotification),
      switchMap(({ id }) =>
        this.notificationService.deleteNotification(id).pipe(
          map(() => NotificationsAdminActions.deleteNotificationSuccess({ id })),
          catchError((error) =>
            of(NotificationsAdminActions.deleteNotificationFailure({
              error: error.message || 'Failed to delete notification',
            }))
          )
        )
      )
    )
  );

  deleteNotificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsAdminActions.deleteNotificationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to delete notification. Please try again.',
            'Delete Notification Error'
          );
        })
      ),
    { dispatch: false }
  );

  reactivateNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsAdminActions.reactivateNotification),
      switchMap(({ id }) =>
        this.notificationService.reactivateNotification(id).pipe(
          map(() => NotificationsAdminActions.reactivateNotificationSuccess({ id })),
          catchError((error) =>
            of(NotificationsAdminActions.reactivateNotificationFailure({
              error: error.message || 'Failed to reactivate notification',
            }))
          )
        )
      )
    )
  );

  reactivateNotificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsAdminActions.reactivateNotificationFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to reactivate notification. Please try again.',
            'Reactivate Notification Error'
          );
        })
      ),
    { dispatch: false }
  );

  loadUserNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUserActions.loadUserNotifications),
      withLatestFrom(this.store.select(selectUserOrgId)),
      switchMap(([, orgId]) =>
        this.notificationService.getUserNotifications(orgId).pipe(
          map((notifications) => NotificationsUserActions.loadUserNotificationsSuccess({ notifications })),
          catchError((error) =>
            of(NotificationsUserActions.loadUserNotificationsFailure({
              error: error.message || 'Failed to load notifications',
            }))
          )
        )
      )
    )
  );

  loadUserNotificationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotificationsUserActions.loadUserNotificationsFailure),
        tap(() => {
          this.errorDialogService.showError(
            'Failed to load notifications. Please try again.',
            'Notifications Error'
          );
        })
      ),
    { dispatch: false }
  );

  markNotificationRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUserActions.markNotificationRead),
      switchMap(({ id }) =>
        this.notificationService.markAsRead(id).pipe(
          map(() => NotificationsUserActions.markNotificationReadSuccess({ id })),
          catchError((error) =>
            of(NotificationsUserActions.markNotificationReadFailure({
              error: error.message || 'Failed to mark notification as read',
            }))
          )
        )
      )
    )
  );

  markNotificationUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUserActions.markNotificationUnread),
      switchMap(({ id }) =>
        this.notificationService.markAsUnread(id).pipe(
          map(() => NotificationsUserActions.markNotificationUnreadSuccess({ id })),
          catchError((error) =>
            of(NotificationsUserActions.markNotificationUnreadFailure({
              error: error.message || 'Failed to mark notification as unread',
            }))
          )
        )
      )
    )
  );

  showWelcomeModalOnLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsUserActions.loadUserNotificationsSuccess),
      filter(() => this.isInitialLogin),
      withLatestFrom(this.store.select(selectWelcomeModalShown)),
      filter(([{ notifications }, alreadyShown]) =>
        !alreadyShown && notifications.some((notification) => !notification.isRead)
      ),
      map(([{ notifications }]) => {
        this.isInitialLogin = false;
        const unreadCount = notifications.filter((notification) => !notification.isRead).length;
        this.dialog.open(NotificationWelcomeModalComponent, {
          width: '400px',
          disableClose: false,
          data: { unreadCount },
        });
        return NotificationsUserActions.welcomeModalShown();
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private notificationService: NotificationService,
    private errorDialogService: ErrorDialogService,
    private dialog: MatDialog,
  ) {}
}
